import { useState, useRef, useEffect, useMemo } from 'react'
import { ChevronDown, TrendingUp, TrendingDown, Minus, Search, CheckSquare, Square } from 'lucide-react'

interface Instrument {
  id: string
  name: string
  startDate: string
  endDate: string
  barCount?: number
  // Reason code from the data-quality audit (e.g. 'END_TRUNCATED', 'THIN'),
  // or null when the contract is clean. Flagged contracts stay selectable —
  // this is a warning, not a filter.
  dataQuality?: string | null
  // The audit's verdict, set by /backtest/instruments. `dataQuality` alone
  // cannot answer "is this good?": it is null both for a clean contract and
  // for one the audit has never seen.
  dataQualityStatus?: 'usable' | 'caveat' | 'excluded' | 'unaudited' | null
}

interface MarketAnalysisInstrument {
  id: string
  trend: 'bullish' | 'bearish' | 'neutral'
  total_return_pct: number
}

interface InstrumentSelectorProps {
  instruments: Instrument[]
  // Single mode (default)
  value?: string
  onChange?: (id: string) => void
  // Multi mode
  multi?: boolean
  selectedIds?: string[]
  onSelectionChange?: (ids: string[]) => void
  // Common
  disabled?: boolean
  marketAnalysis?: { all_instruments: MarketAnalysisInstrument[] } | null
}

// Amber warning for contracts the data-quality audit rejected. The contract stays
// selectable and runnable; this only says the audit found a defect, so a strange
// result is explained rather than mysterious. Codes come from utils/data_quality.py.
export const DataQualityBadge = ({ reason }: { reason: string }) => (
  <span
    title={`Data quality: ${reason} — excluded from usable_ids(); see research/data_quality/DATA_QUALITY.md`}
    className="bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap"
  >
    {reason}
  </span>
)

// Slate note for contracts the audit passed but flagged (SINGLE_PRICE_HEAVY).
// Deliberately a different colour from DataQualityBadge: those contracts are
// runnable, they are simply left out of bulk selection, and a row skipped by
// "select all" with nothing on screen explaining why is the confusing case.
export const CaveatBadge = ({ reason }: { reason: string }) => (
  <span
    title={`Caveat: ${reason} — usable, but not included in bulk "select all"; see research/data_quality/DATA_QUALITY.md`}
    className="bg-slate-500/20 text-slate-300 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap"
  >
    {reason}
  </span>
)

// What a family chip or "Select All" picks by default. Audit-usable contracts
// and ones the audit has not seen yet (freshly downloaded data) are in; the
// audit's rejects, its caveats, and anything with no bars are out. Every
// contract stays individually selectable — this is a default, not a filter.
const isBulkSelectable = (i: Instrument): boolean =>
  (i.dataQualityStatus === 'usable' || i.dataQualityStatus === 'unaudited') &&
  (i.barCount ?? 0) > 0

const extractRootSymbol = (id: string): string => {
  const base = id.split('.')[0]
  const match = base.match(/^(.+?)[FGHJKMNQUVXZ]\d{1,2}$/)
  return match ? match[1] : base
}

export default function InstrumentSelector({
  instruments,
  value,
  onChange,
  multi = false,
  selectedIds = [],
  onSelectionChange,
  disabled = false,
  marketAnalysis,
}: InstrumentSelectorProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  // Off by default: bulk actions pick only contracts worth running. Ticking it
  // restores the old behaviour of selecting every contract of a root.
  const [includeFlagged, setIncludeFlagged] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Focus search on open
  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus()
    }
  }, [open])

  // Build trend lookup from market analysis
  const trendMap = new Map<string, MarketAnalysisInstrument>()
  if (marketAnalysis?.all_instruments) {
    for (const inst of marketAnalysis.all_instruments) {
      trendMap.set(inst.id, inst)
    }
  }

  const selected = instruments.find(i => i.id === value)

  const filtered = search
    ? instruments.filter(i =>
        i.id.toLowerCase().includes(search.toLowerCase()) ||
        (i.name && i.name.toLowerCase().includes(search.toLowerCase()))
      )
    : instruments

  const TrendIcon = ({ instrumentId }: { instrumentId: string }) => {
    const data = trendMap.get(instrumentId)
    if (!data) return <span className="text-[#444]">-</span>
    if (data.trend === 'bullish')
      return <TrendingUp size={14} className="text-green-400" />
    if (data.trend === 'bearish')
      return <TrendingDown size={14} className="text-red-400" />
    return <Minus size={14} className="text-[#666]" />
  }

  // Multi-mode helpers
  const toggleInstrument = (id: string) => {
    if (!onSelectionChange) return
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter(s => s !== id))
    } else {
      onSelectionChange([...selectedIds, id])
    }
  }

  const selectAll = () => {
    if (!onSelectionChange) return
    const pool = includeFlagged ? filtered : filtered.filter(isBulkSelectable)
    // Merge with existing selections (preserve selections not in current filter)
    const merged = [...new Set([...selectedIds, ...pool.map(i => i.id)])]
    onSelectionChange(merged)
  }

  // Per root: every contract, and the subset a bulk action picks by default.
  const rootGroups = useMemo(() => {
    const groups = new Map<string, { all: string[]; good: string[] }>()
    for (const inst of instruments) {
      const root = extractRootSymbol(inst.id)
      const entry = groups.get(root) || { all: [], good: [] }
      entry.all.push(inst.id)
      if (isBulkSelectable(inst)) entry.good.push(inst.id)
      groups.set(root, entry)
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [instruments])

  const toggleRoot = (_root: string, group: { all: string[]; good: string[] }) => {
    if (!onSelectionChange) return
    const pick = includeFlagged ? group.all : group.good
    if (pick.length === 0) return
    const allSelected = pick.every(id => selectedIds.includes(id))
    if (allSelected) {
      // Clear the WHOLE family, not just what the chip would add — a lit chip
      // reads as "this root is on", so clicking it should leave nothing of that
      // root selected, including a flagged contract ticked by hand.
      const removeSet = new Set(group.all)
      onSelectionChange(selectedIds.filter(id => !removeSet.has(id)))
    } else {
      onSelectionChange([...new Set([...selectedIds, ...pick])])
    }
  }

  const clearAll = () => {
    if (!onSelectionChange) return
    if (search) {
      // Only clear filtered instruments
      const filteredIds = new Set(filtered.map(i => i.id))
      onSelectionChange(selectedIds.filter(id => !filteredIds.has(id)))
    } else {
      onSelectionChange([])
    }
  }

  // Button display text
  const buttonContent = () => {
    if (multi) {
      if (selectedIds.length === 0) {
        return <span className="text-[#666]">{instruments.length === 0 ? 'No instruments available' : 'Select instruments...'}</span>
      }
      return (
        <span className="flex items-center gap-2">
          <span className="bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded text-xs font-medium">
            {selectedIds.length}
          </span>
          <span className="text-white font-mono text-sm">
            {selectedIds.length === 1 ? selectedIds[0] : `${selectedIds.length} instruments selected`}
          </span>
        </span>
      )
    }

    // Single mode
    if (selected) {
      return (
        <span className="flex items-center gap-2">
          <span className="font-mono">{selected.id}</span>
          {trendMap.has(selected.id) && <TrendIcon instrumentId={selected.id} />}
          {selected.dataQuality &&
            (selected.dataQualityStatus === 'caveat'
              ? <CaveatBadge reason={selected.dataQuality} />
              : <DataQualityBadge reason={selected.dataQuality} />)}
          {selected.startDate && (
            <span className="text-[#666] text-xs ml-1">
              {selected.startDate} → {selected.endDate}
            </span>
          )}
        </span>
      )
    }

    return <span className="text-[#666]">{instruments.length === 0 ? 'No instruments available' : 'Select instrument...'}</span>
  }

  const colCount = (multi ? 1 : 0) + 4 + (trendMap.size > 0 ? 1 : 0)

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className="input w-full text-left flex items-center justify-between gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="truncate">{buttonContent()}</span>
        <ChevronDown size={14} className={`text-[#666] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[520px] bg-[#1a1a1a] border border-[#333] rounded-lg shadow-xl overflow-hidden"
          style={{ right: 'auto', left: 0 }}
        >
          {/* Search + Multi controls */}
          <div className="p-2 border-b border-[#333]">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#666]" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search instruments..."
                className="input w-full pl-7 py-1.5 text-sm"
              />
            </div>
            {multi && (
              <>
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    onClick={selectAll}
                    title={
                      includeFlagged
                        ? 'Select every listed contract, including ones the data-quality audit rejected'
                        : 'Select audit-clean contracts that have bars. Rejected and zero-bar contracts are skipped — tick "Include flagged" to take everything.'
                    }
                    className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Select All{search ? ' (filtered)' : ''}
                  </button>
                  <span className="text-[#333]">|</span>
                  <button
                    type="button"
                    onClick={clearAll}
                    className="text-xs text-[#888] hover:text-white transition-colors"
                  >
                    Clear{search ? ' (filtered)' : ' All'}
                  </button>
                  <span className="text-[#333]">|</span>
                  <label
                    title="Off: bulk actions pick only audit-clean contracts that have bars. On: they pick every contract, including ones the data-quality audit rejected."
                    className="flex items-center gap-1 text-xs text-[#888] hover:text-white transition-colors cursor-pointer select-none"
                  >
                    <input
                      type="checkbox"
                      checked={includeFlagged}
                      onChange={e => setIncludeFlagged(e.target.checked)}
                      className="accent-blue-500 w-3 h-3 cursor-pointer"
                    />
                    Include flagged
                  </label>
                </div>
                {rootGroups.length > 1 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {rootGroups.map(([root, group]) => {
                      const pick = includeFlagged ? group.all : group.good
                      const empty = pick.length === 0
                      const allSelected = !empty && pick.every(id => selectedIds.includes(id))
                      const partial = pick.length < group.all.length
                      return (
                        <button
                          key={root}
                          type="button"
                          disabled={empty}
                          onClick={() => toggleRoot(root, group)}
                          title={
                            empty
                              ? `${root}: none of its ${group.all.length} contracts passed the data-quality audit — tick "Include flagged" to select them anyway`
                              : partial
                                ? `${root}: ${pick.length} of ${group.all.length} contracts are audit-clean with bars; the rest were excluded by the data-quality audit`
                                : `${root}: all ${pick.length} contracts`
                          }
                          className={`px-1.5 py-0.5 rounded text-xs font-mono transition-colors ${
                            empty
                              ? 'bg-[#1e1e1e] text-[#444] cursor-not-allowed'
                              : allSelected
                                ? 'bg-blue-500/30 text-blue-300 hover:bg-blue-500/40'
                                : 'bg-[#252525] text-[#888] hover:bg-[#333] hover:text-white'
                        }`}
                        >
                          {root}{' '}
                          <span className="text-[#666]">
                            {pick.length}
                            {partial && <span className="text-[#444]">/{group.all.length}</span>}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Table */}
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#1a1a1a]">
                <tr className="border-b border-[#333]">
                  {multi && <th className="w-8 p-2"></th>}
                  <th className="text-left p-2 text-[#666] font-medium">Instrument</th>
                  <th className="text-left p-2 text-[#666] font-medium">Start</th>
                  <th className="text-left p-2 text-[#666] font-medium">End</th>
                  <th className="text-right p-2 text-[#666] font-medium">Bars</th>
                  {trendMap.size > 0 && (
                    <th className="text-center p-2 text-[#666] font-medium">Trend</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={colCount} className="p-3 text-center text-[#666]">
                      No instruments found
                    </td>
                  </tr>
                ) : (
                  filtered.map(inst => {
                    const isSelected = multi ? selectedIds.includes(inst.id) : inst.id === value
                    return (
                      <tr
                        key={inst.id}
                        onClick={() => {
                          if (multi) {
                            toggleInstrument(inst.id)
                          } else {
                            onChange?.(inst.id)
                            setOpen(false)
                            setSearch('')
                          }
                        }}
                        className={`border-b border-[#222] cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-blue-500/15'
                            : 'hover:bg-[#252525]'
                        }`}
                      >
                        {multi && (
                          <td className="p-2 text-center">
                            {isSelected ? (
                              <CheckSquare size={16} className="text-blue-400" />
                            ) : (
                              <Square size={16} className="text-[#555]" />
                            )}
                          </td>
                        )}
                        <td className="p-2 font-mono text-white">
                          <span className="flex items-center gap-1.5">
                            {inst.id}
                            {inst.dataQuality &&
                              (inst.dataQualityStatus === 'caveat'
                                ? <CaveatBadge reason={inst.dataQuality} />
                                : <DataQualityBadge reason={inst.dataQuality} />)}
                          </span>
                        </td>
                        <td className="p-2 text-[#999]">{inst.startDate || '-'}</td>
                        <td className="p-2 text-[#999]">{inst.endDate || '-'}</td>
                        <td className="p-2 text-[#999] text-right">
                          {inst.barCount ? inst.barCount.toLocaleString() : '-'}
                        </td>
                        {trendMap.size > 0 && (
                          <td className="p-2 text-center">
                            <TrendIcon instrumentId={inst.id} />
                          </td>
                        )}
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="px-2 py-1.5 border-t border-[#333] text-xs text-[#666] flex justify-between">
            <span>
              {filtered.length} instrument{filtered.length !== 1 ? 's' : ''}
              {multi && selectedIds.length > 0 && (
                <span className="text-blue-400 ml-2">{selectedIds.length} selected</span>
              )}
            </span>
            {trendMap.size > 0 && (
              <span>Trend from market analysis</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
